import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { openModal } from '../../redux/modals';
import { updateDeviceField } from '../../redux/device';
import { uploadFirmware } from '../../api/uploadFirmware';
import ModalLayout from './ModalLayout';
import {
  updateDevice,
  updateDeviceState as updateState,
  deviceRunBuild,
} from '../../api/devices';
import Ide from '../Ide/Ide';
import DeviceInfoTable from './DeviceModal/DeviceInfoTable';
import DeviceStatusSection from './DeviceModal/DeviceStatusSection';
import DeviceActions from './DeviceModal/DeviceActions';
import DeviceYandex from './DeviceModal/DeviceYandex';
import { debounce } from 'lodash';
import { toast } from 'react-toastify';
import FirmwareBuildModal from './FirmwareBuildModal';
import DeviceTrigger from './DeviceModal/DeviceTrigger';
import { fetchTriggers } from '../../api/trigger';

const validateInput = (value) => {
  return /^[\x20-\x7E]+$/.test(value);
};

const DeviceFormFields = ({
  name,
  group,
  notify_on_connection,
  wifi_ssid,
  wifi_password,
  handleNameChange,
  handleGroupChange,
  handleNotifyOnConnection,
  handleSsidChange,
  handlePasswordChange,
  getBorderColor,
  needMqtt,
}) => (
  <div className="space-y-4">
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <label
          htmlFor="name"
          className="block text-sm font-medium text-gray-700"
        >
          Название:
        </label>
        <input
          id="name"
          type="text"
          value={name}
          onChange={handleNameChange}
          className={`mt-1 block w-full rounded-md border p-2  text-gray-700 ${getBorderColor('name')}`}
        />
      </div>

      {needMqtt && (
        <div>
          <label
            htmlFor="wifi_ssid"
            className="block text-sm font-medium text-gray-700"
          >
            Wi-Fi SSID:
          </label>
          <input
            id="wifi_ssid"
            type="text"
            value={wifi_ssid}
            onChange={handleSsidChange}
            className={`mt-1 block w-full rounded-md border p-2  text-gray-700 ${getBorderColor('wifi_ssid')}`}
          />
        </div>
      )}

      <div>
        <label
          htmlFor="group"
          className="block text-sm font-medium text-gray-700"
        >
          Группа/комната:
        </label>
        <input
          id="group"
          type="text"
          value={group}
          onChange={handleGroupChange}
          className={`mt-1 block w-full rounded-md border p-2  text-gray-700 ${getBorderColor('group')}`}
        />
      </div>

      {needMqtt && (
        <div>
          <label
            htmlFor="wifi_password"
            className="block text-sm font-medium text-gray-700"
          >
            Wi-Fi пароль:
          </label>
          <input
            type="password"
            id="wifi_password"
            value={wifi_password}
            onChange={handlePasswordChange}
            className={`mt-1 block w-full rounded-md border p-2  text-gray-700 ${getBorderColor('wifi_password')}`}
          />
        </div>
      )}

      <div className="flex gap-2">
        <input
          className="w-6"
          checked={notify_on_connection}
          onChange={handleNotifyOnConnection}
          id="notify_on_connection"
          type="checkbox"
        />
        <label
          htmlFor="notify_on_connection"
          className="w-full block text-sm text-gray-700"
        >
          Уведомлять в Telegram о подключении и отключении устройства
        </label>
      </div>
    </div>
  </div>
);

function DeviceModal({
  closeDeviceModal,
  handleFileChange,
  file,
  removeDevice,
  cloneDevice,
  firmwareInfo,
}) {
  const dispatch = useDispatch();
  const devices = useSelector((state) => state.device.devices);
  const deviceId = useSelector((state) => state.device.deviceId);
  const deviceState = useSelector((state) => state.device.state);
  const triggers = useSelector((state) => state.device.triggers);
  const device = devices.find((d) => d.id === deviceId);

  const openEventLogModal = () => dispatch(openModal('eventLogModal'));
  const openFirmwareInfoModal = () => dispatch(openModal('firmwareInfoModal'));
  const openBuildModal = () => dispatch(openModal('buildModal'));
  const openDeviceHistoryModal = () =>
    dispatch(openModal('deviceHistoryModal'));

  const [showYandex, setShowYandex] = useState(false);
  const [showTrigger, setShowTrigger] = useState(false);
  const [showIde, setShowIde] = useState(false);
  const [name, setName] = useState(device?.name || '');
  const [group, setGroup] = useState(device?.group || '');
  const [notify_on_connection, setNotifyOnConnection] = useState(
    device?.notify_on_connection
  );
  const [wifi_ssid, setWifiSsid] = useState(device?.wifi_ssid || '');
  const [wifi_password, setWifiPassword] = useState(
    device?.wifi_password || ''
  );

  const [firmwareFile, setFirmwareFile] = useState('');
  const [fieldStatus, setFieldStatus] = useState({
    name: { isSaving: false, hasError: false },
    group: { isSaving: false, hasError: false },
    notify_on_connection: { isSaving: false, hasError: false },
    wifi_ssid: { isSaving: false, hasError: false },
    wifi_password: { isSaving: false, hasError: false },
  });

  useEffect(() => {
    setFirmwareFile(
      firmwareInfo.find((f) => f.current === true)?.file ||
        'https://iopp.io/media/build_files/base/esp8266/esp8266.ino'
    );
  }, [firmwareInfo]);

  useEffect(() => {
    if (file !== null && deviceId !== undefined) {
      uploadFirmware(deviceId, file);
    }
  }, [file]);

  useEffect(() => {
    setName(device?.name);
    setGroup(device?.group || '');
    setNotifyOnConnection(device?.notify_on_connection);
  }, [device]);

  const toggleIde = () => {
    setShowIde((prev) => !prev);
    setTimeout(() => {
      const ide = document.getElementById('deviceModalCode');
      if (ide) {
        ide.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Заменяем throttle на debounce с задержкой 3 секунды
  const debouncedUpdate = useCallback(
    debounce(async (deviceId, data, fieldName) => {
      try {
        await updateDevice(deviceId, data);

        dispatch(
          updateDeviceField({
            deviceId,
            field: fieldName,
            value: data[fieldName],
          })
        );
      } catch (error) {
        console.log(error);
        if (error.status === 422) {
          const msg = error?.body?.detail[0];
          toast.warning(`${msg.msg}: "${msg.input}"`);
        } else {
          toast.warning(error.message || 'Ошибка при обновлении');
        }
        setFieldStatus((prev) => ({
          ...prev,
          [fieldName]: { isSaving: false, hasError: true },
        }));
      } finally {
        setFieldStatus((prev) => ({
          ...prev,
          [fieldName]: { isSaving: false, hasError: prev[fieldName].hasError },
        }));
      }
    }, 2000),
    [dispatch]
  );

  const handleNameChange = (event) => {
    setFieldStatus((prev) => ({
      ...prev,
      name: { isSaving: true, hasError: false },
    }));
    const newName = event.target.value;
    setName(newName);
    debouncedUpdate(deviceId, { name: newName }, 'name');
  };

  const handleGroupChange = (event) => {
    setFieldStatus((prev) => ({
      ...prev,
      group: { isSaving: true, hasError: false },
    }));
    const newGroup = event.target.value;
    setGroup(newGroup);
    debouncedUpdate(deviceId, { group: newGroup }, 'group');
  };

  const handleNotifyOnConnection = (event) => {
    const newValue = !notify_on_connection;
    setNotifyOnConnection(newValue);
    setFieldStatus((prev) => ({
      ...prev,
      notify_on_connection: { isSaving: true, hasError: false },
    }));
    debouncedUpdate(
      deviceId,
      { notify_on_connection: newValue },
      'notify_on_connection'
    );
  };

  const handleSsidChange = (event) => {
    setFieldStatus((prev) => ({
      ...prev,
      wifi_ssid: { isSaving: true, hasError: false },
    }));
    const newWifiSsid = event.target.value;
    setWifiSsid(newWifiSsid);
    debouncedUpdate(deviceId, { wifi_ssid: newWifiSsid }, 'wifi_ssid');
  };

  const handlePasswordChange = (event) => {
    setFieldStatus((prev) => ({
      ...prev,
      wifi_password: { isSaving: true, hasError: false },
    }));
    const newWifiPassword = event.target.value;
    setWifiPassword(newWifiPassword);
    debouncedUpdate(
      deviceId,
      { wifi_password: newWifiPassword },
      'wifi_password'
    );
  };

  const getBorderColor = (fieldName) => {
    const { isSaving, hasError } = fieldStatus[fieldName];
    if (isSaving) return 'border-yellow-500';
    if (hasError) return 'border-red-500';
    return 'border-gray-300';
  };

  const updateDeviceState = (deviceId, changedValues) => {
    updateState(deviceId, changedValues);
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success('Скопировано в буфер обмена!');
      })
      .catch((err) => {
        console.error('Failed to copy text:', err);
      });
  };

  if (!device) {
    return null;
  }

  const formatToLocalTime = (dateString) => {
    if (!dateString) return null;

    try {
      const formattedDate = dateString.replace(
        /(\d+)\/(\d+)\/(\d+)/,
        '$2/$1/$3'
      );
      const date = new Date(formattedDate);

      if (date instanceof Date && !isNaN(date)) {
        return date.toLocaleString('ru-RU');
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  return (
    <>
      <ModalLayout closeModal={closeDeviceModal} className="w-full">
        <div className="space-y-6">
          <DeviceFormFields
            name={name}
            group={group}
            notify_on_connection={notify_on_connection}
            wifi_ssid={wifi_ssid}
            wifi_password={wifi_password}
            handleNameChange={handleNameChange}
            handleGroupChange={handleGroupChange}
            handleNotifyOnConnection={handleNotifyOnConnection}
            handleSsidChange={handleSsidChange}
            handlePasswordChange={handlePasswordChange}
            getBorderColor={getBorderColor}
            needMqtt={device?.need_mqtt_in_build || false}
          />
          <div className="grid grid-cols-1">
            <div className="overflow-hidden rounded-lg border border-gray-200 shadow-sm">
              <table className="w-full">
                <tbody>
                  <tr>
                    <td className="border-b border-gray-200 bg-gray-150 p-3 text-gray-700">
                      Идентификатор
                    </td>
                    <td
                      className="cursor-pointer border-b border-gray-200 p-3  text-gray-900"
                      onClick={() => handleCopyToClipboard(deviceId)}
                    >
                      {deviceId}
                    </td>
                  </tr>
                  <tr>
                    <td className="border-b border-gray-200 bg-gray-150 p-3 text-gray-700">
                      Статус устройства
                    </td>
                    <td className="border-b border-gray-200 p-3">
                      {!device.connected ? (
                        <span className=" text-gray-600">Не в сети</span>
                      ) : (
                        <span className=" text-green-600">Подключено</span>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="bg-gray-50 p-3 text-gray-700">
                      Последнее время соединения
                    </td>
                    <td className="p-3">
                      <div>{device.last_online}</div>
                      {device.last_online &&
                        formatToLocalTime(device.last_online) && (
                          <div className="text-sm text-gray-500 mt-1">
                            Локальное время:{' '}
                            {formatToLocalTime(device.last_online)}
                          </div>
                        )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <DeviceStatusSection
            deviceId={deviceId}
            updateDeviceState={updateDeviceState}
          />
          <DeviceActions
            removeDevice={removeDevice}
            cloneDevice={cloneDevice}
            isFresh={Object.keys(deviceState).length === 0}
            openYandex={() => setShowYandex(true)}
            openTrigger={() => setShowTrigger(true)}
            toggleIde={toggleIde}
            openEventLogModal={openEventLogModal}
            openBuildModal={openBuildModal}
            openFirmwareInfoModal={openFirmwareInfoModal}
            openDeviceHistoryModal={openDeviceHistoryModal}
            deviceId={deviceId}
          />
        </div>
        <div
          id="deviceModalCode"
          className={`transition-all duration-500 ease-in-out ${
            showIde ? 'opacity-100' : 'hidden h-[0] opacity-0'
          }`}
          style={{ scrollMarginTop: '100px' }}
        >
          <Ide
            handleFileChange={handleFileChange}
            removeDevice={removeDevice}
            file={firmwareFile}
            deviceId={deviceId}
          />
        </div>
      </ModalLayout>
      {showYandex && (
        <DeviceYandex
          deviceId={deviceId}
          closeModal={() => setShowYandex(false)}
        />
      )}

      {showTrigger && (
        <DeviceTrigger
          deviceId={deviceId}
          triggers={triggers}
          closeModal={() => setShowTrigger(false)}
        />
      )}
    </>
  );
}

export default DeviceModal;
